import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {createBrowserRouter, Navigate, RouterProvider, useLocation} from 'react-router';
import type {AreaDeAcceso} from '@cnc/contracts';
import type {ReactNode} from 'react';
import {EXPORTACIONES} from './componentes/areas';
import {ConArea} from './componentes/con-area';
import {FranjaDeAmbiente} from './componentes/franja-de-ambiente';
import {Marco} from './componentes/marco';
import {RequiereSesion} from './componentes/requiere-sesion';
import {SoloAdministrador} from './componentes/solo-administrador';
import {ProveedorDeIdioma} from './i18n/proveedor-idioma';
import {PaginaAlertasInventario} from './paginas/alertas-inventario';
import {PaginaRecolecciones} from './paginas/recolecciones';
import {PaginaBigBag} from './paginas/big-bag';
import {PaginaBitacora} from './paginas/bitacora';
import {PaginaCalidad} from './paginas/calidad';
import {PaginaCashflow} from './paginas/cashflow';
import {PaginaCumplimiento} from './paginas/cumplimiento';
import {PaginaDore} from './paginas/dore';
import {PaginaDoreExportacion} from './paginas/dore-exportacion';
import {PaginaDoreNueva} from './paginas/dore-nueva';
import {PaginaCambiosExportaciones} from './paginas/cambios-exportaciones';
import {PaginaNuevaExportacion} from './paginas/nueva-exportacion';
import {PaginaEmpresas} from './paginas/empresas';
import {PaginaCargarArchivos} from './paginas/cargar-archivos';
import {PaginaCargas} from './paginas/cargas';
import {PaginaConfiguracion} from './paginas/configuracion';
import {PaginaCarpetasDropbox} from './paginas/carpetas-dropbox';
import {PaginaCuenta} from './paginas/cuenta';
import {PaginaDocumentosAdmin} from './paginas/documentos-admin';
import {PaginaEditarExportacion} from './paginas/editar-exportacion';
import {PaginaEstado} from './paginas/estado';
import {PaginaGex} from './paginas/gex';
import {PaginaGuia} from './paginas/guia';
import {PaginaFinanciaciones} from './paginas/financiaciones';
import {PaginaFinanciadores} from './paginas/financiadores';
import {PaginaExportacion} from './paginas/exportacion';
import {PaginaInformes} from './paginas/informes';
import {PaginaIngresar} from './paginas/ingresar';
import {PaginaInicio} from './paginas/inicio';
import {PaginaInventario} from './paginas/inventario';
import {PaginaLogros} from './paginas/logros';
import {PaginaMineros} from './paginas/mineros';
import {PaginaNavieras} from './paginas/navieras';
import {PaginaCertificados} from './paginas/certificados';
import {PaginaDeError, PaginaNoEncontrada} from './paginas/no-encontrada';
import {PaginaConfiguracionDeCampos} from './paginas/configuracion-de-campos';
import {PaginaContenedores} from './paginas/contenedores';
import {PaginaPlaneador} from './paginas/planeador';
import {PaginaPrefijos} from './paginas/prefijos';
import {PaginaReportes} from './paginas/reportes';
import {PaginaReservas} from './paginas/reservas';
import {PaginaSeguimiento} from './paginas/seguimiento';
import {PaginaUsuarios} from './paginas/usuarios';

// Cada pantalla con su seccion o pestana (decision 0062).
const en = (area: AreaDeAcceso | AreaDeAcceso[], pagina: ReactNode) => <ConArea area={area}>{pagina}</ConArea>;
const INVENTARIO: AreaDeAcceso[] = ['inventario.consolidado', 'inventario.mineros', 'inventario.recolecciones'];

const clienteConsultas = new QueryClient({
  defaultOptions: {queries: {staleTime: 10_000, refetchOnWindowFocus: false}},
});

const enrutador = createBrowserRouter([
  {path: '/ingresar', element: <PaginaIngresar />},
  {
    path: '/',
    element: <RequiereSesion />,
    children: [
      {
        element: <Marco />,
        children: [
          {index: true, element: <PaginaInicio />},
          {path: 'inventario', element: en('inventario.consolidado', <PaginaInventario />)},
          {path: 'inventario/recolecciones', element: en('inventario.recolecciones', <PaginaRecolecciones />)},
          {path: 'inventario/mineros', element: en('inventario.mineros', <PaginaMineros />)},
          {path: 'inventario/cargas', element: en('inventario.consolidado', <PaginaCargas />)},
          {path: 'inventario/:bbNo', element: en(INVENTARIO, <PaginaBigBag />)},
          {path: 'seguimiento', element: en('exportaciones.seguimiento', <PaginaSeguimiento />)},
          {path: 'configuracion-de-campos', element: en('catalogos', <PaginaConfiguracionDeCampos />)},
          {path: 'configuracion-de-campos/prefijos', element: en('catalogos', <PaginaPrefijos />)},
          {path: 'configuracion-de-campos/financiadores', element: en('catalogos', <PaginaFinanciadores />)},
          {path: 'configuracion-de-campos/empresas', element: en('catalogos', <PaginaEmpresas />)},
          // Direccion anterior de los prefijos (enlaces guardados).
          {path: 'seguimiento/prefijos', element: <Navigate to="/configuracion-de-campos/prefijos" replace />},
          {path: 'seguimiento/navieras', element: en('exportaciones.navieras', <PaginaNavieras />)},
          {path: 'seguimiento/certificados', element: en('exportaciones.certificados', <PaginaCertificados />)},
          {path: 'seguimiento/contenedores', element: en('exportaciones.contenedores', <PaginaContenedores />)},
          {path: 'seguimiento/reservas', element: en('exportaciones.reservas', <PaginaReservas />)},
          {path: 'seguimiento/alertas-de-reservas', element: en('exportaciones.reservas', <PaginaAlertasInventario />)},
          {path: 'seguimiento/gex', element: en('exportaciones.gex', <PaginaGex />)},
          {path: 'seguimiento/financiaciones', element: en('exportaciones.financiaciones', <PaginaFinanciaciones />)},
          {path: 'seguimiento/cambios', element: en('exportaciones.seguimiento', <PaginaCambiosExportaciones />)},
          {path: 'seguimiento/nueva', element: en('exportaciones.seguimiento', <PaginaNuevaExportacion />)},
          {path: 'seguimiento/:codigo', element: en(EXPORTACIONES, <PaginaExportacion />)},
          {path: 'seguimiento/:codigo/editar', element: en('exportaciones.seguimiento', <PaginaEditarExportacion />)},
          {path: 'planeador', element: en('planeador', <PaginaPlaneador />)},
          {path: 'cashflow', element: en('cashflow', <PaginaCashflow />)},
          {path: 'dore', element: en('dore', <PaginaDore />)},
          {path: 'dore/nueva', element: en('dore', <PaginaDoreNueva />)},
          {path: 'dore/:codigo', element: en('dore', <PaginaDoreExportacion />)},
          {path: 'guia', element: <PaginaGuia />},
          {path: 'cumplimiento', element: en('cumplimiento', <PaginaCumplimiento />)},
          {path: 'cargar', element: <PaginaCargarArchivos />},
          {path: 'analisis/reportes', element: en('analisis.reportes', <PaginaReportes />)},
          {path: 'analisis/informes', element: en('analisis.informes', <PaginaInformes />)},
          {path: 'calidad', element: en('analisis.calidad', <PaginaCalidad />)},
          {path: 'logros', element: <PaginaLogros />},
          // Direcciones anteriores al menu nuevo (6/10/2026): enlaces guardados.
          {path: 'inventario/reservas', element: <Redirigir a="/seguimiento/reservas" />},
          {path: 'inventario/alertas', element: <Redirigir a="/seguimiento/alertas-de-reservas" />},
          {path: 'inventario/reportes', element: <Redirigir a="/analisis/reportes" />},
          {path: 'informes', element: <Redirigir a="/analisis/informes" />},
          {path: 'cuenta', element: <PaginaCuenta />},
          {path: 'cuenta/contrasena', element: <PaginaCuenta />},
          {path: 'cuenta/dos-pasos', element: <PaginaCuenta />},
          {
            path: 'administracion',
            element: <SoloAdministrador />,
            children: [
              {path: 'usuarios', element: <PaginaUsuarios />},
              {path: 'bitacora', element: <PaginaBitacora />},
              {path: 'configuracion', element: <PaginaConfiguracion />},
              {path: 'documentos', element: <PaginaDocumentosAdmin />},
              {path: 'carpetas', element: <PaginaCarpetasDropbox />},
            ],
          },
          {path: 'estado', element: <PaginaEstado />},
          {path: '*', element: <PaginaNoEncontrada />},
        ],
      },
    ],
    errorElement: <PaginaDeError />,
  },
]);

// Una direccion que se movio sigue funcionando, con sus filtros (?id= de la
// reserva, filtros de los reportes) y su ancla.
function Redirigir({a}: {a: string}) {
  const {search, hash} = useLocation();
  return <Navigate to={`${a}${search}${hash}`} replace />;
}

export function App() {
  return (
    <QueryClientProvider client={clienteConsultas}>
      <ProveedorDeIdioma>
        <FranjaDeAmbiente />
        <RouterProvider router={enrutador} />
      </ProveedorDeIdioma>
    </QueryClientProvider>
  );
}
